import { useContext, useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet";
import SecondHeader from "../../Header/SecondHeader";
import trans, { isRTL } from "../../../utils";
import AntdTable from "../../antd/Table/Table";
import UsersHead from "../../Users/UsersParts/UsersHead";
import VehicleBulkUpload from "../../Vehicles/VehicleBulkUpload/VehicleBulkUpload";
import { TechnicianForm } from "./Form/TechnicianForm";
import { popupcontext } from "../../../context/Popupscontext";
import { useGetAllTechnicianQuery } from "../../../Redux/service/Technician/Technician";
import { Button, Tag } from "antd";
import { filtrationcontext } from "../../../context/Filtercontext";
import { EnvironmentOutlined, UserOutlined } from "@ant-design/icons";
import Actions from "../../antd/Table/Actions";
import Avatar from "../../antd/Avatar";
import usePermission from "../../../hooks/usePermission";
import ShiftPoiPopup from "./ShiftPoiPopup";
import AssignPOIsDrawer from "../../Users/UsersPopUp/AssignPOIsDrawer";

const Technicians = ({ bulkUpload, setBulkUpload }) => {
	const { handleShow, deleteItem, setDeleteItem, edited, setEdited } =
		useContext(popupcontext);
	const { create, edit, delete: canDelete } = usePermission("SETUPUSERROLES");
	// RBAC: POIS edit/add also unlocks POI assignment (mirrors the API gate)
	const { edit: canEditPois, create: canAddPois } = usePermission("POIS");

	const {
		limit,
		setLimit,
		setSort: SetSortFilter,
		searchQuery,
		setSearchQuery,
	} = useContext(filtrationcontext);
	const [currentPage, setCurrentPage] = useState(1);
	const [shiftConfig, setShiftConfig] = useState(null);
	// null = closed; { userId, name } = single-staff mode (row action);
	// {} = bulk mode (page button, staff picked inside the drawer).
	// userId is the technician's tc_user_id (tc_users.id), NOT technicians.id —
	// POI assignments live on tc_user_poi which references tc_users.
	const [poiAssign, setPoiAssign] = useState(null);
	const [sort, setSort] = useState({
		by: "",
		order: "",
	});

	const { data, isFetching, refetch } = useGetAllTechnicianQuery(
		{
			page: currentPage,
			pageSize: limit,
			sortBy: sort.by,
			sortOrder: sort.order,
			search: searchQuery,
		},
		{ refetchOnMountOrArgChange: true }
	);

	const handleTableChange = (pagination, filters, sorter) => {
		if (sorter.order) {
			setSort({
				by: sorter.field,
				order: sorter.order === "ascend" ? "asc" : "desc",
			});
		} else {
			setSort({
				by: "id",
				order: "desc",
			});
		}
	};
	const columns = [
		{
			title: trans("Name"),
			dataIndex: "name",
			width: 300,
			sorter: true,
			fixed: !isRTL ? "left" : undefined,
			render: (_, record) => (
				<div className="d-flex align-items-center gap-2">
					<Avatar
						size={35}
						src={
							record.profile_photo
								? `${process.env.REACT_APP_BACKEND_URL}/${record.profile_photo}`
								: ""
						}
						fallback={<UserOutlined />}
					/>
					<span className="table-element-name ps-0">{record.name}</span>
				</div>
			),
		},
		{
			title: trans("phone number"),
			dataIndex: "phone",
			width: 150,
		},
		{
			title: trans("Email"),
			dataIndex: "email",
			sorter: true,
			width: 150,
		},
		{
			title: trans("Employee Code"),
			dataIndex: "employee_id",
			sorter: true,
			width: 150,
		},
		{
			title: trans("department"),
			dataIndex: "department",
			sorter: true,
			width: 150,
			render: (department) => <span>{department || "- - - - -"}</span>,
		},
		{
			title: trans("work location"),
			dataIndex: "work_location",
			sorter: true,
			width: 150,
			render: (location) => <span>{location || "- - - - -"}</span>,
		},
		{
			title: trans("On Call"),
			dataIndex: "on_call",
			sorter: true,
			width: 150,
			render: (call) =>
				call ? (
					<Tag color="green">On-Call</Tag>
				) : (
					<Tag color="red">Off-Call</Tag>
				),
		},
		{
			title: trans("Supervisor"),
			dataIndex: "supervisor_name",
			sorter: true,
			width: 150,
		},
		{
			title: trans("Actions"),
			width: 150,
			fixed: !isRTL ? "right" : undefined,
			render: ({ id, name, tc_user_id }) => (
				<Actions
					technicianId={id}
					canAddTask={create}
					canEdit={edit}
					canDelete={canDelete}
					onConfigShift={() => setShiftConfig({ id, name })}
					onAssignPois={
						tc_user_id
							? () => setPoiAssign({ userId: tc_user_id, name })
							: undefined
					}
				/>
			),
		},
	];

	useEffect(() => {
		return () => {
			SetSortFilter({ type: "", value: "" });
			setSearchQuery("");
		};
	}, [SetSortFilter, setSearchQuery]);

	useEffect(() => {
		return () => {
			setCurrentPage(1);
		};
	}, [searchQuery]);

	useEffect(() => {
		async function fetchDataAfterDelete() {
			if (deleteItem.condition === true && deleteItem.value === "technician") {
				const res = await refetch();
				const currentData = res?.data?.technicians || [];

				if (currentData.length === 0 && currentPage > 1) {
					setCurrentPage((prev) => prev - 1);
				}

				setDeleteItem({ condition: null, value: null });
			}
		}
		fetchDataAfterDelete();
	}, [
		deleteItem.condition,
		deleteItem.value,
		currentPage,
		refetch,
		setDeleteItem,
	]);

	useEffect(() => {
		async function fetchdata() {
			if (edited) {
				await refetch();
				setEdited(false);
			}
		}
		fetchdata();
	}, [edited]);

	return (
		<>
			<Helmet>
				<title>Field Staff</title>
				<meta name="description" content="Field Staff" />
			</Helmet>
			{/* <SecondHeader
        title={trans("Technicians")}
        add={trans("Add new technician")}
        onClick={() => handleShow("addForm")}
        type={"vehicles"}
        secButton={"Bulk Upload"}
        secClick={() => setBulkUpload(true)}
      /> */}
			<UsersHead
				extra={
					(create || edit || canEditPois || canAddPois) && (
						<Button
							type="primary"
							ghost
							icon={<EnvironmentOutlined />}
							onClick={() => setPoiAssign({})}
						>
							{trans("Bulk Assign POIs")}
						</Button>
					)
				}
			/>
			<div className="bg-white-rounded-top mt-3 table-parent">
				<AntdTable
					columns={columns}
					dataSource={data?.technicians || []}
					loading={isFetching}
					pageSize={limit}
					totalItems={data?.total}
					currentPage={currentPage}
					onPageChange={(page) => setCurrentPage(page)}
					onLimitChange={(newLimit) => {
						setLimit(newLimit);
						setCurrentPage(1);
					}}
					onTableChange={handleTableChange}
				/>
			</div>
			<TechnicianForm />
			<VehicleBulkUpload
				bulkUpload={bulkUpload}
				setBulkUpload={setBulkUpload}
			/>
			{shiftConfig && (
				<ShiftPoiPopup
					technicianId={shiftConfig.id}
					name={shiftConfig.name}
					onClose={() => setShiftConfig(null)}
				/>
			)}
			<AssignPOIsDrawer
				userId={poiAssign?.userId}
				userName={poiAssign?.name}
				open={!!poiAssign}
				onClose={() => setPoiAssign(null)}
			/>
		</>
	);
};

export default Technicians;
